"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PolarPosition = exports.SpatialRemoteParticipant = exports.SpatialEnvironment = exports.SpatialScale = exports.SpatialDirection = exports.SpatialPosition = void 0;
/**
 * > 🚀Pre-release
 * >
 * > This model is a part of the [Pre-release program](doc:overview-pilot-and-pre-release-programs), as a Closed Beta.
 *
 * The SpatialPosition model represents a participant's audio position. The position is defined using Cartesian coordinates.
 *
 * You can define the direction of each axis in the coordinate system using the [setSpatialEnvironment](doc:js-client-sdk-conferenceservice#setspatialenvironment) method. By default, the environment consists of the following axes:
 *
 * - X-axis: Extends positive to the right
 * - Y-axis: Extends positive upwards
 * - Z-axis: Extends positive forwards
 *
 * The [setSpatialEnvironment](doc:js-client-sdk-conferenceservice#setspatialenvironment) method allows the application to choose the meaning of each axis and match the usage of the application.
 *
 */
class SpatialPosition {
    /**
     * @ignore
     */
    constructor() {
        this.x = 0;
        this.y = 0;
        this.z = 0;
    }
    /**
     * @ignore
     */
    static fromData(x, y, z) {
        let position = new SpatialPosition();
        position.x = x;
        position.y = y;
        position.z = z;
        return position;
    }
}
exports.SpatialPosition = SpatialPosition;
/**
 * > 🚀Pre-release
 * >
 * > This model is a part of the [Pre-release program](doc:overview-pilot-and-pre-release-programs), as a Closed Beta.
 *
 * The SpatialDirection model defines the direction a participant is facing. The model is specified as a set of three Euler rotations about the corresponding axis in the order of z-x-y:
 *
 * The following properties define a rotation about the specified positive axis:
 *
 * - `x`: A rotation about the x-axis
 * - `y`: A rotation about the y-axis
 * - `z`: A rotation about the z-axis
 *
 * These properties correspond to yaw, pitch, and roll:
 *
 * - Yaw: A rotation about the up axis, where the default environment is the Y rotation. For more details, see the yaw.mp4 video.
 * - Pitch: A rotation about the right axis, where the default environment is the X rotation. For more details, see the pitch.mp4 video.
 * - Roll: A rotation about the forward axis, where the default environment is the Z rotation. For more details, see the roll.mp4 video.
 */
class SpatialDirection {
    /**
     * @ignore
     */
    constructor() {
        this.x = 0;
        this.y = 0;
        this.z = 0;
    }
    /**
     * @ignore
     */
    static fromData(x, y, z) {
        let direction = new SpatialDirection();
        direction.x = x;
        direction.y = y;
        direction.z = z;
        return direction;
    }
}
exports.SpatialDirection = SpatialDirection;
/**
 * > 🚀Pre-release
 * >
 * > This model is a part of the [Pre-release program](doc:overview-pilot-and-pre-release-programs), as a Closed Beta.
 *
 * The SpatialScale model defines how to convert units from the application's coordinate system (pixels or centimeters) into meters used by the spatial audio coordinate system. For example, let's assume that SpatialScale is set to (100,100,100), which indicates that 100 of the applications units (cm) map to 1 meter for the audio coordinates. If the listener's location is (0,0,0)cm and a remote participant's location is (200,200,200)cm, the listener has an impression of hearing the remote participant from the (2,2,2)m location.
 *
 * **Note**: A scale value must have a value greater than zero. The default is (1,1,1).
 */
class SpatialScale {
    /**
     * @ignore
     */
    constructor() {
        this.x = 1;
        this.y = 1;
        this.z = 1;
    }
    /**
     * @ignore
     */
    static fromData(x, y, z) {
        let scale = new SpatialScale();
        scale.x = x;
        scale.y = y;
        scale.z = z;
        return scale;
    }
}
exports.SpatialScale = SpatialScale;
/**
 * @ignore
 * Model storing all spatial audio environment parameters used internally for converting between application and audio space.
 */
class SpatialEnvironment {
    constructor() {
        this.forward = SpatialPosition.fromData(0, 0, 1);
        this.up = SpatialPosition.fromData(0, 1, 0);
        this.right = SpatialPosition.fromData(1, 0, 0);
        this.scale = new SpatialScale();
    }
    static fromData(scale, forward, up, right) {
        let environment = new SpatialEnvironment();
        environment.scale = scale;
        environment.forward = forward;
        environment.up = up;
        environment.right = right;
        return environment;
    }
}
exports.SpatialEnvironment = SpatialEnvironment;
/**
 * @ignore
 * Model storing all spatial audio parameters used internally for remote participants.
 */
class SpatialRemoteParticipant {
    constructor() {
        this.position = new SpatialPosition();
    }
    static fromData(participant, position) {
        let talker = new SpatialRemoteParticipant();
        talker.participant = participant;
        talker.position = position;
        return talker;
    }
}
exports.SpatialRemoteParticipant = SpatialRemoteParticipant;
/**
 * @ignore
 * Model representing spherical coordinate system used internally.
 */
class PolarPosition {
    constructor() {
        this.azimuth = 0;
        this.elevation = 0;
        this.distance = 0;
    }
    fromData(azimuth, elevation, distance) {
        this.azimuth = azimuth;
        this.elevation = elevation;
        this.distance = distance;
    }
}
exports.PolarPosition = PolarPosition;
